import React, { useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import ChatbotUI from "../../additional_components/common/Modals/Chat"
import { useLocation } from "react-router-dom"
import { useOnboarding } from "../../contexts/OnboardingContext"
import { useAnalytics, ANALYTICS_EVENTS } from "../../analytics"

const AskAIButton = ({ style }) => {
  const { trackEvent } = useAnalytics()
  const [open, setOpen] = useState(false)
  const toggle = () => setOpen(prev => {
    const next = !prev
    if (next) trackEvent(ANALYTICS_EVENTS.FEATURE.ASK_AI_OPENED)
    return next
  })
  const [hover, setHover] = useState(false)
  const askAIFabRef = useRef(null)

  // Keyword Explorer-specific Ask AI tutorial (final step in the flow)
  const [showKeywordAskAITutorial, setShowKeywordAskAITutorial] = useState(false)
  const { markPageCompleted } = useOnboarding()

  const location = useLocation()
  const path = location && location.pathname ? location.pathname : ""
  const isKeywordExplorerPage = path === "/keyword-analysis" || path.startsWith("/keyword-analysis")

  // Listen for global keyword tutorial step changes and show Ask AI tutorial only on step 4
  // Also mark tutorial as completed when leaving the page
  useEffect(() => {
    // Mark tutorial as completed when leaving the keyword explorer page
    if (!isKeywordExplorerPage) {
      try {
        const tutorialSeen = sessionStorage.getItem("keywordExplorerOnboardingSeen") === "true"
        const currentStep = sessionStorage.getItem("keywordTutorialCurrentStep")

        // If tutorial is active (not seen and has a current step), mark it as completed
        if (!tutorialSeen && currentStep && (currentStep === "0" || currentStep === "1" || currentStep === "2" || currentStep === "3" || currentStep === "4")) {
          sessionStorage.setItem("keywordExplorerOnboardingSeen", "true")
          sessionStorage.removeItem("keywordTutorialCurrentStep")
          window.dispatchEvent(new CustomEvent("keywordTutorialStep", { detail: { step: -1 } }))

          // Try to mark as completed via API (non-blocking)
          if (typeof markPageCompleted === "function") {
            markPageCompleted("keyword_explorer").catch(() => null)
          } else {
            fetch("/api/onboarding/complete", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ page: "keyword_explorer" })
            }).catch(() => null)
          }
        }
      } catch (e) {
        // ignore storage errors
      }
      setShowKeywordAskAITutorial(false)
      return
    }

    const checkTutorialStep = () => {
      try {
        const tutorialSeen = sessionStorage.getItem("keywordExplorerOnboardingSeen") === "true"
        const currentStep = sessionStorage.getItem("keywordTutorialCurrentStep")
        if (!tutorialSeen && currentStep === "4") {
          setShowKeywordAskAITutorial(true)
        } else {
          setShowKeywordAskAITutorial(false)
        }
      } catch (e) {
        setShowKeywordAskAITutorial(false)
      }
    }

    const handleKeywordTutorialStep = e => {
      const step = typeof e?.detail?.step === "number" ? e.detail.step : e?.detail?.step
      if (step === 4) {
        setShowKeywordAskAITutorial(true)
      } else if (typeof step === "number" && step !== 4) {
        setShowKeywordAskAITutorial(false)
      }
    }

    // Initial check + event listener
    checkTutorialStep()
    window.addEventListener("keywordTutorialStep", handleKeywordTutorialStep)

    return () => {
      window.removeEventListener("keywordTutorialStep", handleKeywordTutorialStep)
    }
  }, [isKeywordExplorerPage])

  // Position the Ask AI tutorial explanation box and arrow to the left of the button
  useEffect(() => {
    if (!showKeywordAskAITutorial || !isKeywordExplorerPage) return

    const updatePositions = () => {
      const btn = askAIFabRef.current
      if (!btn) return

      const rect = btn.getBoundingClientRect()
      if (!rect || rect.width === 0 || rect.height === 0) return

      // Highlight box around the Ask AI button (reuses .kt-highlight-box styles)
      const highlightBox = document.querySelector(".kt-highlight-box--askai")
      if (highlightBox) {
        const padding = 4
        highlightBox.style.top = `${rect.top - padding}px`
        highlightBox.style.left = `${rect.left - padding}px`
        highlightBox.style.width = `${rect.width + padding * 2}px`
        highlightBox.style.height = `${rect.height + padding * 2}px`
      }

      // Explanation box to the left of the button
      const explanationBox = document.querySelector(".kt-explanation-box--askai")
      if (explanationBox) {
        explanationBox.style.top = "0px"
        explanationBox.style.left = "0px"

        requestAnimationFrame(() => {
          const boxRect = explanationBox.getBoundingClientRect()
          const viewportWidth = window.innerWidth
          const viewportHeight = window.innerHeight
          const isMobileNow = viewportWidth <= 768
          const isTablet = viewportWidth > 768 && viewportWidth <= 1024

          if (isMobileNow) {
            // Mobile: Position box below the Ask AI button, centered
            const gap = 16
            let boxTop = rect.bottom + gap
            const boxLeft = 16 // Use CSS left: 16px from media query

            // Ensure box stays within viewport
            if (boxTop + boxRect.height > viewportHeight - 16) {
              boxTop = Math.max(16, viewportHeight - boxRect.height - 16)
            }

            explanationBox.style.top = `${boxTop}px`
          } else {
            // Desktop/Tablet: Position to the left of button
            const gap = isTablet ? 40 : 56
            // Push box slightly upwards so full content (Prev / Done) stays visible
            const verticalOffset = -40

            let boxTop = rect.top + rect.height / 2 - boxRect.height / 2 + verticalOffset
            let boxLeft = rect.left - boxRect.width - gap

            // Ensure box stays within viewport — same clamp as the other
            // tutorial steps (profile/settings, chips, etc.), so a tall box
            // vertically centered on the anchor button can't push its top
            // or bottom edge off-screen with no way to scroll it back.
            if (boxTop < 16) {
              boxTop = 16
            } else if (boxTop + boxRect.height > viewportHeight - 16) {
              boxTop = viewportHeight - boxRect.height - 16
            }
            if (boxLeft < 16) {
              boxLeft = 16
            }

            explanationBox.style.top = `${boxTop}px`
            explanationBox.style.left = `${boxLeft}px`

            // Arrow from tutorial box to the Ask AI button (rightward)
            const arrow = document.querySelector(".kt-arrow-to-askai")
            if (arrow) {
              // Draw the line from the box's edge to the BUTTON's actual
              // center — not the box's own (possibly-clamped/offset)
              // center. The old "+20 nudge" was a fixed approximation that
              // only lined up when the box sat at its unclamped position;
              // once the viewport clamp above moves the box, a fixed
              // horizontal line at a hardcoded offset misses the button
              // entirely. A diagonal line (via rotation) always points at
              // the real target instead.
              const boxCenterY = boxTop + boxRect.height / 2
              const boxRight = boxLeft + boxRect.width
              const highlightLeft = rect.left - 4
              const buttonCenterY = rect.top + rect.height / 2
              const endX = highlightLeft - 10 // small gap so arrow tip doesn't touch highlight border
              const dx = endX - boxRight
              const dy = buttonCenterY - boxCenterY
              const length = Math.max(Math.sqrt(dx * dx + dy * dy), 0)
              const angle = (Math.atan2(dy, dx) * 180) / Math.PI

              arrow.style.top = `${boxCenterY}px`
              arrow.style.left = `${boxRight}px`
              arrow.style.width = `${length}px`
              arrow.style.height = "3px"
              arrow.style.transform = `rotate(${angle}deg)`
              arrow.style.transformOrigin = "0 50%"
            }
          }
        })
      }
    }

    updatePositions()
    window.addEventListener("resize", updatePositions)
    window.addEventListener("scroll", updatePositions, true)

    return () => {
      window.removeEventListener("resize", updatePositions)
      window.removeEventListener("scroll", updatePositions, true)
    }
  }, [showKeywordAskAITutorial, isKeywordExplorerPage])

  const handleKeywordAskAITutorialPrev = () => {
    // Move back to profile tutorial (step 3)
    setShowKeywordAskAITutorial(false)
    try {
      sessionStorage.setItem("keywordTutorialCurrentStep", "3")
      window.dispatchEvent(new CustomEvent("keywordTutorialStep", { detail: { step: 3 } }))
    } catch (e) {
      // ignore storage errors
    }
  }

  const handleKeywordAskAITutorialComplete = async () => {
    // Final step: mark keyword explorer tutorial as completed
    setShowKeywordAskAITutorial(false)
    try {
      sessionStorage.setItem("keywordExplorerOnboardingSeen", "true")
      sessionStorage.removeItem("keywordTutorialCurrentStep")
      window.dispatchEvent(new CustomEvent("keywordTutorialStep", { detail: { step: -1 } }))
      if (typeof markPageCompleted === "function") {
        await markPageCompleted("keyword_explorer")
      }
    } catch (e) {
      // ignore storage errors
      // eslint-disable-next-line no-console
      console.error("Error completing keyword explorer Ask AI tutorial:", e)
    }
  }

  const handleKeywordAskAITutorialSkipAll = async () => {
    // Skip all from Ask AI step behaves like completing the keyword explorer tutorial
    await handleKeywordAskAITutorialComplete()
  }

  // Do not show Ask AI on the dashboard, root/home page, Weekly Insights, Custom Insights, or the dedicated ChatBot page
  if (
    path === "/dashboard" ||
    path === "/" ||
    path.startsWith("/dashboard/") ||
    path === "/weekly-insights" ||
    path.startsWith("/weekly-insights") ||
    path === "/custom-insights" ||
    path.startsWith("/custom-insights") ||
    path === "/chatbot" ||
    path.startsWith("/chatbot/")
  ) {
    return null
  }
  // Also hide on blog listing and blog detail pages
  if (path === "/blog" || path.startsWith("/blog/")) return null

  // place the button inside the page area (not overlapping the header)
  const fabPosition = {
    position: "fixed",
    right: "20px",
    bottom: "20px",
    // Default z-index keeps FAB above page content; during tutorial we lower slightly
    // so tutorial overlays (highlight & explanation box) can sit above the button.
    zIndex: showKeywordAskAITutorial ? 1001 : 9999,
  }

  const baseFab = {
    ...fabPosition,
    height: 40,
    padding: "0 12px",
    background: "linear-gradient(135deg,#e83e8c,#ff6fa3)",
    border: "none",
    color: "#fff",
    boxShadow: "0 8px 24px rgba(232,62,140,0.28)",
    borderRadius: 24,
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    opacity: 0.92,
    transition: "transform .12s ease, box-shadow .12s ease, opacity .12s ease",
    cursor: "pointer",
  }

  const hoverStyle = hover
    ? { transform: "translateY(-2px) scale(1.04)", boxShadow: "0 12px 34px rgba(232,62,140,0.36)", opacity: 1 }
    : {}

  const finalStyle = style ? { ...baseFab, ...style, ...hoverStyle } : { ...baseFab, ...hoverStyle }

  return (
    <>
      <button
        ref={askAIFabRef}
        aria-label="Ask AI"
        title="Ask AI"
        onClick={toggle}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={finalStyle}
        className="ask-ai-fab d-flex align-items-center justify-content-center"
      >
        <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
            <path d="M21 15a2 2 0 0 1-2 2H8l-5 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10z" fill="#fff" opacity="0.95" />
            <circle cx="8.5" cy="11.5" r="0.95" fill="#ff6fa3" />
            <circle cx="12" cy="11.5" r="0.95" fill="#ff6fa3" />
            <circle cx="15.5" cy="11.5" r="0.95" fill="#ff6fa3" />
          </svg>
        </span>
        <span style={{ fontWeight: 700, fontSize: 13, lineHeight: 1 }}>{"Ask AI"}</span>
      </button>

      {/* Keyword Explorer – Ask AI tutorial (final step, box on the left of Ask AI button) */}
      {isKeywordExplorerPage && showKeywordAskAITutorial && (
        <>
          <div className="kt-backdrop-overlay"></div>
          <div className="kt-highlight-box kt-highlight-box--askai"></div>
          <div className="kt-explanation-box kt-explanation-box--askai">
            <button
              type="button"
              className="kt-skip-all-btn"
              onClick={handleKeywordAskAITutorialSkipAll}
            >
              Skip All
            </button>
            <div className="kt-explanation-text">
              Use <strong>Ask AI</strong> to know more about each section and feature.
            </div>
            <div className="kt-explanation-arrow kt-arrow-to-askai"></div>
            <div className="kt-explanation-nav">
              <button
                type="button"
                className="kt-nav-btn kt-nav-btn--prev"
                onClick={handleKeywordAskAITutorialPrev}
              >
                &lt; Prev
              </button>
              <button
                type="button"
                className="kt-nav-btn kt-nav-btn--next"
                onClick={handleKeywordAskAITutorialComplete}
              >
                Done
              </button>
            </div>
          </div>
        </>
      )}

      {/* Compute an exact prompt for the current page and pass it to the modal so it is sent automatically */}
      {(() => {
        const pageTitle = document && document.title ? document.title : ""
        const pagePath = path || ""
        const lcPath = pagePath.toLowerCase()
        const lcTitle = pageTitle.toLowerCase()

        // Mapping of common pages to a short description and the exact prompt to auto-send
        // Exact route matches for MetaInsight / CustomInsight pages (use lcPath)
        const mapping = [
          // MetaInsight / Weekly Insights routes (exact/startsWith matches)
          {
            test: p => p === "/weekly-insights" || p.startsWith("/weekly-insights"),
            desc: "Weekly Insights Dashboard: Overview of all weekly metrics including trending, new releases, popular apps, and revenue data.",
            prompt: "In Weekly Insights Dashboard: give me an overview of all weekly metrics and insights?"
          },
          {
            test: p => p === "/trending" || p.startsWith("/trending"),
            desc: "Weekly Insights — Trending: Items that are currently gaining traction this week.",
            prompt: "In Weekly Insights: what is trending?"
          },
          {
            test: p => p === "/newrelease" || p.startsWith("/newrelease") || p === "/new-release" || p.startsWith("/new-release"),
            desc: "Weekly Insights — New Releases: Newly launched apps or updates this week.",
            prompt: "In Weekly Insights: what is new release?"
          },
          {
            test: p => p === "/top-apps" || p.startsWith("/top-apps"),
            desc: "Weekly Insights — Top Apps: Top performing apps for the selected window.",
            prompt: "In Weekly Insights: what are the Top Apps?"
          },
          {
            test: p => p === "/popular" || p.startsWith("/popular"),
            desc: "Weekly Insights — Popular: Apps that are most popular this week.",
            prompt: "In Weekly Insights: what is popular?"
          },
          {
            test: p => p === "/most-popular" || p.startsWith("/most-popular"),
            desc: "Weekly Insights — Most Popular: Apps most interacted with this week.",
            prompt: "In Weekly Insights: what is popular?"
          },
          {
            test: p => p === "/pre-orders" || p.startsWith("/pre-orders"),
            desc: "Weekly Insights — Preorders: Apps available for preorder this week.",
            prompt: "In Weekly Insights: what are preorders?"
          },

          // Custom Insights / Top lists routes
          {
            test: p => p === "/topgross" || p.startsWith("/topgross") || p === "/top-gross" || p.startsWith("/top-gross"),
            desc: "Custom Insights — Top Grossing: Apps grossing the most revenue.",
            prompt: "In Custom Insights: what is Top Grossing?"
          },
          {
            test: p => p === "/topimproved" || p.startsWith("/topimproved") || p === "/all-time-hits" || p.startsWith("/all-time-hits"),
            desc: "Custom Insights — Top Improved: Apps that improved the most recently.",
            prompt: "In Custom Insights: what is Top Improved?"
          },
          {
            test: p => p === "/top-keywords-categories" || p.startsWith("/top-keywords-categories"),
            desc: "Custom Insights — Hits & Trending: Currently trending hits and notable upward movers.",
            prompt: "In Custom Insights: what are Hits & Trending?"
          },
          {
            test: p => p === "/top-rated" || p.startsWith("/top-rated"),
            desc: "Custom Insights — Top Rated: Apps with the highest average ratings.",
            prompt: "In Custom Insights: what is Top Rated?"
          },
          {
            test: p => p === "/top-downloaded" || p.startsWith("/top-downloaded"),
            desc: "Custom Insights — Top Downloaded: Apps with the most downloads.",
            prompt: "In Custom Insights: what is Top Downloaded?"
          },
          {
            test: p => p === "/top-reviewed" || p.startsWith("/top-reviewed"),
            desc: "Custom Insights — Top Reviewed: Apps receiving the most or highest-quality reviews.",
            prompt: "In Custom Insights: what is Top Reviewed?"
          },

          // end exact route matches

          // App & Keyword Explorer
          {
            test: p => p === "/keyword-analysis" || p.startsWith("/keyword-analysis"),
            desc: "App & Keyword Explorer: Discover apps and keywords, explore search trends, and compare app-level metrics.",
            prompt: "What is App & Keyword Explorer?"
          },
          {
            test: p => p === "/keyword-suggestions" || p.startsWith("/keyword-suggestions"),
            desc: "Keyword Suggestions: Discover suggested keywords related to your app or seed keywords.",
            prompt: "What is Keyword Suggestions?"
          },
          {
            test: p => p.includes("explorer") || p.includes("app & keyword") || p.includes("market-research") || p.includes("app-explorer"),
            desc: "App & Keyword Explorer: Discover apps and keywords, explore search trends, and compare app-level metrics.",
            prompt: "What is App & Keyword Explorer?"
          },
          // Game / App Dashboard exact route
          {
            test: p => p === "/analytics" || p.startsWith("/analytics"),
            desc: "App Dashboard: Overview of a specific app including installs, revenue, rankings, reviews and releases.",
            prompt: "What is App Dashboard? What can I see here?"
          },
          // Category Analysis exact route
          {
            test: p => p === "/categoryanalysis" || p.startsWith("/categoryanalysis") || p === "/category-analysis" || p.startsWith("/category-analysis"),
            desc: "Category Analysis: Analyze performance and trends within an app category — top apps, installs, and keyword opportunities.",
            prompt: "What is Category Analysis?"
          },
          // Category Tracking landing page exact route
          {
            test: p => p === "/category-tracking" || p.startsWith("/category-tracking") || p === "/category-tracking-detail" || p.startsWith("/category-tracking-detail"),
            desc: "Genre Tracking: Track category-level keyword and app performance; use filters and historic trends to find opportunities.",
            prompt: "What is Genre Tracking?"
          },
          // App Tracking
          {
            test: p => p.includes("app-tracking") || p.includes("app tracking") || (p.includes("app") && p.includes("tracking")),
            desc: "App Tracking: Monitor a specific app’s installs, revenue, rankings, reviews and updates over time.",
            prompt: "What is App Tracking?"
          },
          // Keyword Tracking
          {
            test: p => p.includes("keyword-tracking") || p.includes("keyword tracking") || (p.includes("keyword") && p.includes("track")) || p.includes("keyword"),
            desc: "Keyword Tracking: Track specific keywords’ ranking and performance over time for selected apps and markets.",
            prompt: "What is Keyword Tracking?"
          },
          // Publisher Analysis
          {
            test: p => p.includes("publisher") || p.includes("publisher-analysis") || p.includes("publisher analysis"),
            desc: "Publisher Analysis: Inspect a publisher's portfolio — aggregate installs/revenue and top apps.",
            prompt: "What is Publisher Analysis?"
          },
          // Apps from Publisher exact route
          {
            test: p => p === "/publisher-apps" || p.startsWith("/publisher-apps"),
            desc: "Apps From Publisher: View apps published by a specific publisher, their metrics and comparative performance.",
            prompt: "What is Apps From Publisher?"
          },
          // Treat /profile and profile plan/payment routes as subscription/plans pages (no dedicated /subscription route)
          {
            test: p => p === "/profile" || p === "/profile/" || p === "/profile/plans" || p.startsWith("/profile/plans") || p === "/profile/payment" || p.startsWith("/profile/payment"),
            desc: "Subscription Plans: View available KeyQuest plans, features, limits, and pricing.",
            prompt: "What are the subscription plans of KeyQuest?"
          },
          // Profile settings (explicit)
          {
            test: p => p === "/profile/settings" || p.startsWith("/profile/settings"),
            desc: "Profile: Manage your account details, change email/password, and configure notifications.",
            prompt: "How to edit the profile?"
          },
          // Billing (improved prompt to include subscriptions)
          {
            test: p => p.includes("billing") || p.includes("invoice") || p.includes("invoices") || p.includes("payments"),
            desc: "Billing: View invoices, payment methods, billing history, and subscription details for your account.",
            prompt: "What is the Billing section and what are the subscription plans of KeyQuest?"
          },
          // Subscription Plans (match explicit subscription/pricing routes only)
          {
            test: p => p.includes("/subscription") || p.includes("/subscriptions") || p.includes("/plans") || p.includes("/pricing"),
            desc: "Subscription Plans: View available KeyQuest plans, features, limits, and pricing.",
            prompt: "What are the subscription plans of KeyQuest?"
          },

          // Weekly Insights specific pages
          {
            test: p => p.includes("weekly") && p.includes("trending") || p.includes("trending"),
            desc: "Weekly Insights — Trending: Items that are currently gaining traction this week.",
            prompt: "In Weekly Insights: what is trending?"
          },
          {
            test: p => p.includes("weekly") && (p.includes("new-release") || p.includes("new release") || p.includes("new-releases")) || p.includes("new release") || p.includes("new-releases"),
            desc: "Weekly Insights — New Releases: Newly launched apps or updates this week.",
            prompt: "In Weekly Insights: what is new release?"
          },
          {
            test: p => p.includes("weekly") && p.includes("top-revenue") || p.includes("top revenue") || p.includes("top-revenue"),
            desc: "Weekly Insights — Top Revenue: Apps generating the most revenue this week.",
            prompt: "In Weekly Insights: what is top revenue?"
          },
          {
            test: p => p.includes("weekly") && p.includes("popular") || p.includes("popular"),
            desc: "Weekly Insights — Popular: Apps that are most popular or frequently interacted with this week.",
            prompt: "In Weekly Insights: what is popular?"
          },
          {
            test: p => p.includes("weekly") && p.includes("preorder") || p.includes("preorder"),
            desc: "Weekly Insights — Preorders: Apps available for preorder this week.",
            prompt: "In Weekly Insights: what are preorders?"
          },

          // Custom Insights specific pages
          {
            test: p => p.includes("grossing") || p.includes("top-grossing"),
            desc: "Custom Insights — Grossing: Apps that are grossing the most revenue in the selected criteria.",
            prompt: "In Custom Insights: what is grossing?"
          },
          {
            test: p => p.includes("top-improved") || p.includes("top improved") || p.includes("improved"),
            desc: "Custom Insights — Top Improved: Apps that have improved the most in rank or metrics recently.",
            prompt: "In Custom Insights: what is top improved?"
          },
          {
            test: p => p.includes("hits") || p.includes("hits-and-trending") || p.includes("hits & trending") || p.includes("hits and trending"),
            desc: "Custom Insights — Hits & Trending: Currently trending hits and notable upward movers.",
            prompt: "In Custom Insights: what are hits and trending?"
          },
          {
            test: p => p.includes("top-reviewed") || p.includes("top reviewed") || p.includes("reviewed"),
            desc: "Custom Insights — Top Reviewed: Apps receiving the most or highest-quality reviews.",
            prompt: "In Custom Insights: what is top reviewed?"
          },
          {
            test: p => p.includes("top-rated") || p.includes("top rated") || p.includes("rated"),
            desc: "Custom Insights — Top Rated: Apps with the highest average ratings.",
            prompt: "In Custom Insights: what is top rated?"
          },
          {
            test: p => p.includes("top-downloaded") || p.includes("top downloaded") || p.includes("downloaded"),
            desc: "Custom Insights — Top Downloaded: Apps with the most downloads in the selected window.",
            prompt: "In Custom Insights: what is top downloaded?"
          },

          // Generic Weekly/Custom fallback
          {
            test: p => p.includes("weekly") || p.includes("insights"),
            desc: "Weekly Insights: Shows trending items, new releases, top revenue, popular charts and preorders.",
            prompt: "In Weekly Insights: what is trending, what is new release, what is top revenue, what is popular and what are preorders?"
          },
          {
            test: p => p.includes("custom-insights") || p.includes("custom insights") || p.includes("custom-insight"),
            desc: "Custom Insights: Curated lists such as grossing, top improved, hits & trending, top reviewed, top rated and top downloaded.",
            prompt: "In Custom Insights: what is grossing, what is top improved, what are hits and trending, what is top reviewed, what is top rated and what is top downloaded?"
          }
        ]

        // Find first mapping that matches the current path (use path only to avoid matching product title)
        const combined = lcPath
        let match = mapping.find(m => m.test(combined))

        // Default behavior: do NOT auto-call the Ask AI API.
        // We only show a static page description (when available) as the first bot message.
        let pageDescription = ""
        let initialPrompt = ""

        // Special cases: pages that have a custom static explanation.
        if (lcPath === "/keyword-suggestions" || lcPath.startsWith("/keyword-suggestions")) {
          pageDescription =
            "Keyword Suggestions are recommendations or ideas for keywords that you can use to improve the visibility and search ranking of your app. These suggestions are based on trends, popular search terms, and relevance to your app's content. By incorporating these suggested keywords into your app's metadata, description, and other relevant areas, you can increase the chances of your app being discovered by users searching for similar content. The Keyword Suggestions tool helps you identify and utilize trending keywords that can potentially boost your app's visibility and performance in search results."
          initialPrompt = ""
        } else if (lcPath === "/keyword-analysis" || lcPath.startsWith("/keyword-analysis")) {
          pageDescription =
            "The Keyword Explorer tool allows users to search and explore keywords to discover related apps across the Metastore platform. It helps identify trending keywords for better app visibility and includes features like search functionality, filter options by platform, and direct links to individual app pages for deeper insights."
          initialPrompt = ""
        } else if (lcPath === "/category-analysis" || lcPath.startsWith("/category-analysis")) {
          pageDescription =
            "Genre Explorer is a tool that allows users to browse and analyze various categories of apps from the Metastore platform. It helps users view apps organized by category, analyze performance and popularity within selected categories, and access predefined popular categories quickly. This tool is valuable for understanding competitive trends, choosing the right category for app launch or repositioning, and benchmarking app performance against competitors. It can also provide insights into shifting apps to more niche but less competitive categories for faster growth."
          initialPrompt = ""
        } else if (lcPath === "/category-tracking" || lcPath.startsWith("/category-tracking")) {
          pageDescription =
            "Genre Tracking is a tool that allows users to monitor the performance metrics of apps within specific categories. By selecting an app and one or more categories, users can view their app's position in the category, top-performing apps within that category, and historical trends based on selected dates. This tool helps users stay informed about the dynamics of the app market within specific categories."
          initialPrompt = ""
        } else if (lcPath === "/app-tracking" || lcPath.startsWith("/app-tracking")) {
          pageDescription =
            "App Tracking is a feature that allows users to monitor multiple apps simultaneously for comparative analysis. It provides insights into various metrics such as downloads, average user rating, user review volume, keyword relevance, and usage. Users can track app performance trends over specific date ranges to evaluate market positioning and make informed decisions based on the data collected."
          initialPrompt = ""
        } else if (lcPath === "/keyword-tracking" || lcPath.startsWith("/keyword-tracking")) {
          pageDescription =
            "Keyword tracking is a feature that allows users to monitor and track the performance of their app in relation to specific search terms or keywords. It helps app developers and marketers understand how their app ranks in search results for these keywords, as well as the overall visibility and performance of their app in the app store."
          initialPrompt = ""
        } else if (lcPath === "/xr/keyword" || lcPath.startsWith("/xr/keyword")) {
          pageDescription =
            "Keyword tracking is a feature that allows users to monitor and track the performance of their app in relation to specific search terms or keywords. It helps app developers and marketers understand how their app ranks in search results for these keywords, as well as the overall visibility and performance of their app in the app store."
          initialPrompt = ""
        } else if (lcPath === "/xr/category" || lcPath.startsWith("/xr/category")) {
          pageDescription =
            "Genre Tracking is a tool that allows users to monitor the performance metrics of apps within specific categories. By selecting an app and one or more categories, users can view their app's position in the category, top-performing apps within that category, and historical trends based on selected dates. This tool helps users stay informed about the dynamics of the app market within specific categories."
          initialPrompt = ""
        } else if (lcPath === "/creative-insights" || lcPath.startsWith("/creative-insights")) {
          pageDescription =
            "Ad Discovery in the KeyQuest platform is a feature that helps users find, explore, and analyze available ads, campaigns, or creatives across the system so they can research trends, competitors, and performance opportunities."
          initialPrompt = ""
        } else if (lcPath === "/publisher-analysis" || lcPath.startsWith("/publisher-analysis")) {
          pageDescription =
            "Publisher Explorer is a tool that helps you analyze game and app publishers across the Metastore platform. It lets you see a publisher's full portfolio, track installs and revenue at the portfolio level, and understand which titles are driving performance. You can compare publishers side by side, discover rising or declining publishers in a category, and drill into individual apps for deeper insight. This view is useful for competitive research, partnership targeting, and spotting emerging publishers or genres before they become crowded."
          initialPrompt = ""
        } else if (lcPath === "/publisher-apps" || lcPath.startsWith("/publisher-apps")) {
          pageDescription =
            "The Publisher Apps Dashboard gives you a detailed view of all apps from a selected publisher. You can see installs, revenue, ratings, and rankings at the app level while keeping the publisher context. This page helps you understand which titles drive most of the portfolio performance, compare apps within the same publisher, and quickly jump into individual app dashboards for deeper analysis."
          initialPrompt = ""
        } else if (lcPath === "/publisher-socials" || lcPath.startsWith("/publisher-socials")) {
          pageDescription =
            "The Publisher Socials page brings together a publisher's social media presence across channels such as X/Twitter, Discord, YouTube, and more. It helps you quickly discover how actively a publisher communicates with their audience, what type of content they share, and how engaged their community is. This view is useful for understanding marketing strength, community health, and for finding official links when researching a publisher in more depth."
          initialPrompt = ""
        } else if (lcPath === "/trending" || lcPath.startsWith("/trending")) {
          pageDescription =
            "The 'Trending Apps' feature in the Weekly Insights Dashboard allows you to see which apps are currently gaining popularity in the market. This can help you stay informed about emerging trends and potentially identify new opportunities for your own app."
          initialPrompt = ""
        } else if (lcPath === "/new-releases" || lcPath.startsWith("/new-releases")) {
          pageDescription =
            "The 'New Releases' feature in the Weekly Insights provides information about the latest applications launched across both platforms. It helps users and developers stay up to date with the freshest content."
          initialPrompt = ""
        } else if (lcPath === "/top-apps" || lcPath.startsWith("/top-apps")) {
          pageDescription =
            "In the Weekly Insights, the 'Top Selling' section highlights apps that have achieved the highest total revenue for that week. This section provides insight into which apps are currently top-selling in the market."
          initialPrompt = ""
        } else if (lcPath === "/most-popular" || lcPath.startsWith("/most-popular")) {
          pageDescription =
            "The Weekly Insights provides information on four key areas updated every week: Trending Apps, New Releases, Top Revenue, and Popular Apps. This allows users to stay current with app market dynamics by providing insights into what apps are currently popular and trending."
          initialPrompt = ""
        } else if (lcPath === "/top-discounts" || lcPath.startsWith("/top-discounts")) {
          pageDescription =
            "In Weekly Insights, “Top Discounts” are data-driven highlights showing which campaigns, ads, or products performed best due to price reductions or promotional offers during that week."
          initialPrompt = ""
        } else if (lcPath === "/pre-orders" || lcPath.startsWith("/pre-orders")) {
          pageDescription =
            "Top Pre-Orders in Weekly Insights on the KeyQuest platform is a performance summary that highlights which upcoming products or campaigns generated the strongest early demand during the last week."
          initialPrompt = ""
        } else if (lcPath === "/all-time-hits" || lcPath.startsWith("/all-time-hits")) {
          pageDescription =
            "Custom Insights — Top App Score: Apps with the highest app score."
          initialPrompt = ""
        } else if (match) {
          // For all other mapped pages, use the mapping's description as a static explanation
          // and do not auto-send any prompt to the API.
          pageDescription = (match.desc || "").toString().trim()
        }

        return <ChatbotUI toggle={open} togglemodal={toggle} initialPrompt={initialPrompt} pageDescription={pageDescription} />
      })()}
      {/* Ask AI tutorial specific styles (reusing base KT tutorial primitives) */}
      <style>{`
        .kt-highlight-box--askai {
          /* Uses base .kt-highlight-box styles; position computed via JS */
        }
        .kt-explanation-box--askai {
          /* Uses base .kt-explanation-box styles; position computed via JS */
        }
        /* Center the Ask AI tutorial message text inside the box and push it slightly down */
        .kt-explanation-box--askai .kt-explanation-text {
          text-align: center;
          margin-top: 24px;
        }
        /* Pull the Prev / Done controls even further upwards inside the Ask AI tutorial box */
        .kt-explanation-box--askai .kt-explanation-nav {
          margin-top: -4px;
          padding-top: 0;
        }
        /* Ensure Skip All sits at the top-right corner inside the Ask AI tutorial box */
        .kt-explanation-box--askai .kt-skip-all-btn {
          top: 8px;
          right: 8px;
        }
        .kt-arrow-to-askai::after {
          left: 100%;
          border-left: 10px solid #F6255A;
        }

        /* Mobile styles for Ask AI tutorial */
        @media (max-width: 768px) {
          .kt-explanation-box--askai .kt-explanation-text {
            text-align: left;
            margin-top: 12px;
          }
          .kt-explanation-box--askai .kt-explanation-nav {
            margin-top: 12px;
            padding-top: 12px;
          }
          .kt-arrow-to-askai {
            display: none;
          }
        }

        /* Mobile styles for Ask AI button */
        @media (max-width: 768px) {
          .ask-ai-fab {
            right: 16px !important;
            bottom: 16px !important;
            height: 48px !important;
            padding: 0 16px !important;
            min-width: 120px !important;
            font-size: 14px !important;
            box-shadow: 0 4px 16px rgba(232,62,140,0.35) !important;
          }
          .ask-ai-fab span:last-child {
            font-size: 14px !important;
            font-weight: 600 !important;
          }
          .ask-ai-fab svg {
            width: 20px !important;
            height: 20px !important;
          }
        }

        /* Small mobile styles */
        @media (max-width: 480px) {
          .ask-ai-fab {
            right: 12px !important;
            bottom: 12px !important;
            height: 44px !important;
            padding: 0 14px !important;
            min-width: 110px !important;
            font-size: 13px !important;
          }
          .ask-ai-fab span:last-child {
            font-size: 13px !important;
          }
          .ask-ai-fab svg {
            width: 18px !important;
            height: 18px !important;
          }
        }
      `}</style>
    </>
  )
}

AskAIButton.propTypes = {
  style: PropTypes.object,
}

export default AskAIButton
